import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getFormComponent, FORM_CONFIG } from './formConfig';
import NYIT2104Form from './forms/NYIT2104Form'; // For special UUID
import { API_URL } from '../../utils/apiConfig';

// Map form IDs to components - using centralized config
const formComponentMap = {
  // Use centralized config
  ...Object.keys(FORM_CONFIG).reduce((acc, formId) => {
    acc[formId] = getFormComponent(formId);
    return acc;
  }, {}),
  // Special UUID form ID
  '763a37e6-9133-4fc3-b268-1ffa0adc61ac': NYIT2104Form
};

export default function FormRenderer({ activeForm, employeeId, onFormSubmit }) {
  const [formDetails, setFormDetails] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);


  useEffect(() => {
    console.log('🔄 FormRenderer: Form changed to:', activeForm);
    // Reset states when form changes
    setFormDetails(null);
    setError(null);
    setLoading(true);
    
    return () => {
      console.log('🧹 FormRenderer: Cleaning up previous form state');
      setFormDetails(null);
    };
  }, [activeForm]);

  useEffect(() => {
    let mounted = true;
    
    const fetchData = async () => {
      if (!activeForm || !employeeId) {
        setLoading(false);
        return;
      }
    
      try {
        console.log('📥 Fetching fresh data for:', { activeForm, employeeId });
        const token = localStorage.getItem('token');
        
        // Special handling for W4 form (form ID 9)
        if (activeForm === '9') {
          // Fetch W4 specific data
          const [w4Response, formResponse, employeeResponse] = await Promise.all([
            axios.get(`${API_URL}/forms/w4/${employeeId}`, {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get(`${API_URL}/forms/get-form-data/${activeForm}/${employeeId}`, {
              headers: { 
                Authorization: `Bearer ${token}`,
                'Cache-Control': 'no-cache, no-store, must-revalidate'
              },
            }),
            axios.get(`${API_URL}/employees/${employeeId}`, {
              headers: { 
                Authorization: `Bearer ${token}`,
                'Cache-Control': 'no-cache, no-store, must-revalidate'
              },
            })
          ]);
          
          if (!mounted) return;
          
          // Merge W4 specific data with form data
          const formData = {
            ...formResponse.data,
            ...w4Response.data,
            isCompleted: Boolean(formResponse.data.isCompleted),
            documentStatus: formResponse.data.documentStatus || 'Incomplete',
            filePath: formResponse.data.filePath || null,
          };
          
          console.log('📊 Transformed W4 form data:', formData);
          setFormDetails(formData);
          setEmployeeData(employeeResponse.data);
          return;
        }
        
        // Normal form data fetching
        const [formResponse, employeeResponse] = await Promise.all([
          axios.get(`${API_URL}/forms/get-form-data/${activeForm}/${employeeId}`, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Cache-Control': 'no-cache, no-store, must-revalidate'
            },
          }),
          axios.get(`${API_URL}/employees/${employeeId}`, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Cache-Control': 'no-cache, no-store, must-revalidate'
            },
          })
        ]);
    
        if (!mounted) return;
        
        console.log('✨ Received fresh form data:', formResponse.data);
        console.log("📄 Form Data Received:", formResponse.data);

        const formData = {
          ...formResponse.data,
          isCompleted: Boolean(formResponse.data.isCompleted),
          documentStatus: formResponse.data.documentStatus || 'Incomplete',
          filePath: formResponse.data.filePath || null,
        };
        
        console.log('📊 Transformed form data:', formData);
        setFormDetails(formData);
        setEmployeeData(employeeResponse.data);
      } catch (err) {
        console.error('❌ Error fetching form data:', err);
        if (mounted) {
          setError(err.message);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [employeeId, activeForm, refreshTrigger]);
  

  // Listen for form completion events
  useEffect(() => {
    const handleFormComplete = (event) => {
      const params = new URLSearchParams(event.newURL.split('?')[1]);
      const completedFormId = params.get('formId');
      const status = params.get('status');
      
      if (completedFormId === activeForm && status === 'success') {
        setRefreshTrigger(prev => prev + 1);
      }
    };

    window.addEventListener('popstate', handleFormComplete);
    return () => window.removeEventListener('popstate', handleFormComplete);
  }, [activeForm]);
// Duplicate useEffect removed - using the one above
  
// In FormRenderer.js

const handleFormSubmit = async (formId, filePath) => {
  try {
    // Handle both old format (single param) and new format (two params)
    // This ensures backward compatibility
    if (typeof formId === 'object' && formId !== null) {
      // Handle object format from PersonalInformationForm and EmergencyContactForm
      const { formId: id, status } = formId;
      formId = id;
      filePath = null;
    }
    
    // Update form details to show completion
    setFormDetails(prev => ({
      ...prev,
      isCompleted: true,
      filePath: filePath || prev?.filePath
    }));
    
    // Call the parent's onFormSubmit with the updated status
    await onFormSubmit(formId, true); // Pass true to indicate completion
    
    // Fetch fresh data after submission
    const token = localStorage.getItem('token');
    const [formResponse, employeeResponse] = await Promise.all([
      axios.get(`${API_URL}/forms/get-form-data/${activeForm}/${employeeId}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        },
      }),
      axios.get(`${API_URL}/employees/${employeeId}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        },
      })
    ]);

    const updatedFormData = {
      ...formResponse.data,
      isCompleted: true, // Ensure we mark it as completed
      documentStatus: formResponse.data.documentStatus || 'Complete',
      filePath: formResponse.data.filePath || filePath || null,
    };

    setFormDetails(updatedFormData);
    setEmployeeData(employeeResponse.data);
    setRefreshTrigger(prev => prev + 1);
  } catch (error) {
    console.error('Form submission error:', error);
    throw error;
  }
};

  if (loading) {
    return <div>Loading form...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  const SelectedFormComponent = formComponentMap[activeForm];
  if (!SelectedFormComponent) {
    return <div>Form type not found. Active Form ID: {activeForm}</div>;
  }

  if (!formDetails) {
    return <div>No form details available.</div>;
  }
 return (
    <SelectedFormComponent
      key={`${activeForm}-${employeeId}-${refreshTrigger}`} // Use refreshTrigger for controlled updates
      fields={formDetails?.fields}
      responses={formDetails?.responses}
      employee={employeeData}
      formId={activeForm}
      employeeId={employeeId}
      onSubmit={handleFormSubmit}
      documentStatus={formDetails?.documentStatus}
      isCompleted={formDetails?.isCompleted || false}
      filePath={formDetails?.filePath}
      refreshTrigger={refreshTrigger}
    />
  );
}